/**
 * Unified document requirement catalog (phase 1).
 * Every legacy label in document.ts maps here via documentTsLabelToKey.
 */

export const DOCUMENT_REQUIREMENT_KEYS = [
  'id_government',
  'credit_report_auth',
  'w2_two_years',
  'paystubs_last_month',
  'paystubs_eoy_variable',
  'bank_statements_2mo',
  'tax_transcripts_2yr',
  'asset_proof_close',
  'personal_financial_statement',
  'background_report_auth',
  're_schedule_rental',
  'entity_formation_docs',
  'entity_operating_agreement',
  'construction_budget',
  're_schedule_fix_flip',
  're_schedule_commercial',
  're_owned_schedule',
  'mortgage_statement_current',
  'insurance_declaration',
] as const;

export type DocumentRequirementKey = (typeof DOCUMENT_REQUIREMENT_KEYS)[number];

export type DocumentRequirementCategory =
  | 'income'
  | 'assets'
  | 'liabilities'
  | 'credit'
  | 'entity'
  | 'property'
  | 'id'
  | 'explanations';

export type CatalogEntry = {
  displayLabel: string;
  category: DocumentRequirementCategory;
  allowMultiple: boolean;
  sortOrder: number;
};

export const DOCUMENT_REQUIREMENT_CATALOG: Record<
  DocumentRequirementKey,
  CatalogEntry
> = {
  id_government: {
    displayLabel: "Driver's license or government issued ID",
    category: 'id',
    allowMultiple: false,
    sortOrder: 0,
  },
  credit_report_auth: {
    displayLabel: 'Credit Report Authorization',
    category: 'credit',
    allowMultiple: false,
    sortOrder: 10,
  },
  w2_two_years: {
    displayLabel: 'Past two years Forms W-2 from all employers',
    category: 'income',
    allowMultiple: false,
    sortOrder: 20,
  },
  paystubs_last_month: {
    displayLabel: 'Paystubs for past full month',
    category: 'income',
    allowMultiple: false,
    sortOrder: 30,
  },
  paystubs_eoy_variable: {
    displayLabel:
      'End of Year Paystubs if you earn Commission, Overtime or Bonus',
    category: 'income',
    allowMultiple: false,
    sortOrder: 40,
  },
  bank_statements_2mo: {
    displayLabel:
      '2 Bank Statements for All Assets (Checking, Savings, IRA, 401(k) etc.)',
    category: 'assets',
    allowMultiple: false,
    sortOrder: 50,
  },
  tax_transcripts_2yr: {
    displayLabel: 'Most Recent 2 Years Tax Return Transcripts',
    category: 'income',
    allowMultiple: false,
    sortOrder: 60,
  },
  asset_proof_close: {
    displayLabel: 'Asset statements showing sufficient assets to close',
    category: 'assets',
    allowMultiple: false,
    sortOrder: 70,
  },
  personal_financial_statement: {
    displayLabel: 'Personal financial statement',
    category: 'assets',
    allowMultiple: false,
    sortOrder: 80,
  },
  background_report_auth: {
    displayLabel: 'Background report authorization',
    category: 'credit',
    allowMultiple: false,
    sortOrder: 90,
  },
  re_schedule_rental: {
    displayLabel: 'Real estate schedule of rental experience',
    category: 'property',
    allowMultiple: false,
    sortOrder: 100,
  },
  entity_formation_docs: {
    displayLabel:
      'Entity documentation (Certificate of Good Standing, Formation Documentation, IRS EIN Letter)',
    category: 'entity',
    allowMultiple: false,
    sortOrder: 110,
  },
  entity_operating_agreement: {
    displayLabel: 'Executed Entity Operating Agreement',
    category: 'entity',
    allowMultiple: false,
    sortOrder: 120,
  },
  construction_budget: {
    displayLabel: 'Construction Budget',
    category: 'property',
    allowMultiple: false,
    sortOrder: 130,
  },
  re_schedule_fix_flip: {
    displayLabel: 'Real Estate Schedule of Fix and Flip Experience',
    category: 'property',
    allowMultiple: false,
    sortOrder: 140,
  },
  re_schedule_commercial: {
    displayLabel: 'Real Estate Schedule of Commercial Experience',
    category: 'property',
    allowMultiple: false,
    sortOrder: 150,
  },
  re_owned_schedule: {
    displayLabel: 'Real estate owned schedule',
    category: 'property',
    allowMultiple: false,
    sortOrder: 160,
  },
  mortgage_statement_current: {
    displayLabel: 'Current Mortgage Statement',
    category: 'liabilities',
    allowMultiple: false,
    sortOrder: 170,
  },
  insurance_declaration: {
    displayLabel: 'Homeowners Insurance Declaration Page',
    category: 'property',
    allowMultiple: false,
    sortOrder: 180,
  },
};

/** Exact strings as they appear in document.ts → stable key */
export const documentTsLabelToKey: Record<string, DocumentRequirementKey> = {
  "Driver's license or government issued ID": 'id_government',
  'Credit Report Authorization': 'credit_report_auth',
  'Credit report authorization': 'credit_report_auth',
  'Past two years Forms W- from all employers': 'w2_two_years',
  'Past two years Forms W-2 from all employers': 'w2_two_years',
  'Paystubs for past full month': 'paystubs_last_month',
  'End of Year Paystubs if you earn Commission, Overtime or Bonus':
    'paystubs_eoy_variable',
  '2 Bank Statements for All Assets (Checking, Savings, IRA, 401(k) etc.)':
    'bank_statements_2mo',
  'Most Recent 2 Years Tax Return Transcripts': 'tax_transcripts_2yr',
  'Asset statements showing sufficient assets to close': 'asset_proof_close',
  'Asset statements showing sufficient cash-to-close': 'asset_proof_close',
  'Asset Statements showing sufficient Assets to Close': 'asset_proof_close',
  'Personal financial statement': 'personal_financial_statement',
  'Personal Financial Statement': 'personal_financial_statement',
  'Background report authorization': 'background_report_auth',
  'Background Report Authorization': 'background_report_auth',
  'Real estate schedule of rental experience': 're_schedule_rental',
  'Real Estate Ownership schedule showing Rental experience':
    're_schedule_rental',
  'Entity documentation (Certificate of Good Standing, Formation Documentation, IRS EIN Letter)':
    'entity_formation_docs',
  'Executed Entity Operating Agreement': 'entity_operating_agreement',
  'Construction Budget': 'construction_budget',
  'Real Estate Schedule of Fix and Flip Experience': 're_schedule_fix_flip',
  'Real Estate Schedule of Commercial Experience': 're_schedule_commercial',
  'Real Estate Ownership Schedule showing Commercial Experience':
    're_schedule_commercial',
  'Real estate owned schedule': 're_owned_schedule',
  'Current Mortgage Statement': 'mortgage_statement_current',
  'Homeowners Insurance Declaration Page': 'insurance_declaration',
  'Insurance Declaration Page': 'insurance_declaration',
};

export function getCatalogEntry(key: DocumentRequirementKey): CatalogEntry {
  return DOCUMENT_REQUIREMENT_CATALOG[key];
}

export function keysToDisplayLabels(keys: DocumentRequirementKey[]): string[] {
  return keys.map((k) => DOCUMENT_REQUIREMENT_CATALOG[k].displayLabel);
}

export function labelsToKeys(labels: string[]): DocumentRequirementKey[] {
  return labels.map((label) => {
    const key = documentTsLabelToKey[label];
    if (!key) {
      throw new Error(
        `[documentCatalog] Missing documentTsLabelToKey for: ${JSON.stringify(
          label
        )}`
      );
    }
    return key;
  });
}

export function categorizeRequirementKey(
  key: DocumentRequirementKey
): DocumentRequirementCategory {
  return DOCUMENT_REQUIREMENT_CATALOG[key].category;
}
